import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { Card, StripeCardsResponse } from '../components/list-cards-user/interfaces/list-cards-user.interface';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '../../../../../../core/service/auth.service';
import { environment } from '../../../../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CardsStateService {
  private cardsSubject = new BehaviorSubject<Card[] | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<boolean>(false);
  private cardsLoaded = false;

  // Observables públicos
  public cards$ = this.cardsSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Obtiene las tarjetas del usuario desde la API o del caché
   * @returns Observable con las tarjetas del usuario
   */
  getUserCards(): Observable<Card[] | null> {
    // Si ya tenemos las tarjetas y no estamos cargando, devolvemos el valor actual
    if (this.cardsLoaded && !this.loadingSubject.value) {
      return of(this.cardsSubject.value);
    }

    // Si estamos cargando, simplemente devolvemos el observable
    if (this.loadingSubject.value) {
      return this.cards$;
    }

    // Marcamos como cargando
    this.loadingSubject.next(true);
    this.errorSubject.next(false);

    // Obtenemos el token
    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    // Hacemos la petición
    this.http.get<StripeCardsResponse>(
      `${environment.apiUrl}/payment/method`,
      {
        headers,
      }
    ).pipe(
      tap(response => {
        this.cardsSubject.next(response.data || []);
        this.cardsLoaded = true;
      }),
      catchError(error => {
        console.error('Error cargando tarjetas:', error);
        this.errorSubject.next(true);
        this.cardsSubject.next([]);
        return of(null);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
      shareReplay(1)
    ).subscribe();

    return this.cards$;
  }

  /**
   * Actualiza el estado después de agregar o eliminar una tarjeta
   */
  refreshCards(): void {
    this.cardsLoaded = false;
    this.getUserCards();
  }

  /**
   * Selecciona una tarjeta
   * @param card Tarjeta seleccionada
   */
  selectCard(card: Card): void {
    const currentCards = this.cardsSubject.value;
    if (currentCards) {
      // No necesitamos modificar el array, solo notificar la selección
      this.cardsSubject.next([...currentCards]);
    }
  }
}
